import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { api, formatErr } from "../lib/api";
import Seo from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.length < 10) return toast.error("Please write a bit more.");
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-20">
      <Seo
        title="Contact us"
        description="Questions, partnership ideas, press requests — drop the Dkarez team a line and a real human will reply within 48 hours."
      />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="eyebrow">Reach out</span>
        <h1 className="font-serif-display text-5xl sm:text-6xl mt-4 leading-tight">
          Let's <span className="italic text-brand-primary">talk</span>.
        </h1>
        <p className="mt-5 text-brand-muted max-w-2xl">
          Questions, partnership ideas, press requests — drop us a line and the right human will reply within 48 hours.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-14">
        <form onSubmit={submit} className="lg:col-span-3 premium-card p-7 sm:p-9 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Your name</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} className="input-premium" required data-testid="contact-name" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-premium" required data-testid="contact-email" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Subject</label>
            <input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="input-premium" required data-testid="contact-subject" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Message</label>
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} className="input-premium !min-h-[160px]" required minLength={10} data-testid="contact-message" />
          </div>
          <button type="submit" disabled={loading} className="btn-gold !py-3.5" data-testid="contact-submit">
            <Send size={15} strokeWidth={1.8} className="mr-2" />
            {loading ? "Sending…" : "Send message"}
          </button>
        </form>

        <aside className="lg:col-span-2 space-y-4">
          <Detail Icon={Mail} title="Email" body="dkarezcommunitys@gmail.com" href="mailto:dkarezcommunitys@gmail.com" />
          <Detail Icon={Phone} title="Phone" body="+44 7400 455570" href="tel:+447400455570" />
          <Detail Icon={MapPin} title="Office" body="A registered Community Interest Company in the United Kingdom." />
        </aside>
      </div>
    </div>
  );
}

function Detail({ Icon, title, body, href }) {
  const inner = (
    <>
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-primary-light text-brand-primary mb-3">
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <p className="eyebrow">{title}</p>
      <p className="text-brand-text mt-1 break-all">{body}</p>
    </>
  );
  return href ? (
    <a href={href} className="premium-card p-6 block hover:-translate-y-0.5 transition-transform" data-testid={`contact-detail-${title.toLowerCase()}`}>
      {inner}
    </a>
  ) : (
    <div className="premium-card p-6">{inner}</div>
  );
}
